<template>
  <nav>
    <div class="container">
      <router-link :to="isEnglish ? '/en' : '/'" class="logo">minder<span>rente</span>.nl</router-link>
      <ul v-if="isHome" class="nav-desktop">
        <li><a href="#video">{{ t('nav.link1') }}</a></li>
        <li><a href="#tool">{{ t('nav.link2') }}</a></li>
        <li><a href="#brieven">{{ t('nav.link3') }}</a></li>
      </ul>
      <div class="lang-switch desktop">
        <button class="lang-btn" :class="{ active: !isEnglish }" @click="switchTo('nl')">NL</button>
        <button class="lang-btn" :class="{ active: isEnglish }" @click="switchTo('en')">EN</button>
      </div>
      <button v-if="isHome" class="hamburger" type="button" :aria-label="t('nav.menu')" @click="menuOpen = !menuOpen">
        <span :class="{ open: menuOpen }"></span>
      </button>
    </div>
    <div v-if="isHome && menuOpen" class="nav-mobile" @click="menuOpen = false">
      <a href="#video">{{ t('nav.link1') }}</a>
      <a href="#tool">{{ t('nav.link2') }}</a>
      <a href="#brieven">{{ t('nav.link3') }}</a>
      <div class="lang-switch mobile" @click.stop>
        <button class="lang-btn" :class="{ active: !isEnglish }" @click="switchTo('nl')">NL</button>
        <button class="lang-btn" :class="{ active: isEnglish }" @click="switchTo('en')">EN</button>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useLocale } from '../composables/useLocale'

const route = useRoute()
const router = useRouter()
const { t, isEnglish, setLocale } = useLocale()

const isHome = computed(() => route.path === '/' || route.path === '/en' || route.path === '/en/')
const menuOpen = ref(false)

function switchTo(lang) {
  setLocale(lang, router)
  menuOpen.value = false
}
</script>

<style scoped>
nav {
  border-bottom: 1px solid var(--line-soft);
  background: var(--bg);
  position: sticky;
  top: 0;
  z-index: 100;
}
nav .container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 16px;
  padding-bottom: 16px;
}
.logo {
  font-family: 'Fraunces', serif;
  font-weight: 800;
  font-size: 24px;
  letter-spacing: -0.03em;
  text-decoration: none;
  color: var(--ink);
}
.logo span {
  background: var(--yellow);
  padding: 0 10px;
  border-radius: 6px;
  display: inline-block;
  transform: rotate(-2deg);
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  animation: popIn 0.6s 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) backwards;
}
.logo:hover span {
  transform: rotate(2deg);
}

.nav-desktop {
  list-style: none;
  display: flex;
  gap: 28px;
}
.nav-desktop a {
  color: var(--ink);
  text-decoration: none;
  font-weight: 600;
  font-size: 15px;
  border-bottom: 2px solid transparent;
  padding-bottom: 2px;
  transition: border-color 0.15s;
}
.nav-desktop a:hover { border-bottom-color: var(--ink); }

.lang-switch {
  display: inline-flex;
  border-radius: 9999px;
  border: 1px solid var(--line-soft);
  background: var(--bg-warm);
  padding: 3px;
}
.lang-btn {
  padding: 5px 12px;
  border-radius: 9999px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.03em;
  background: transparent;
  border: none;
  color: var(--ink-muted);
  cursor: pointer;
  transition: background 0.25s, color 0.25s;
}
.lang-btn:hover { color: var(--ink); }
.lang-btn.active {
  background: var(--bg);
  color: var(--ink);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}
.lang-switch.mobile {
  margin: 8px 24px 4px;
  align-self: flex-start;
}

.hamburger {
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  width: 40px;
  height: 40px;
  position: relative;
}
.hamburger span,
.hamburger span::before,
.hamburger span::after {
  display: block;
  width: 22px;
  height: 2px;
  background: var(--ink);
  border-radius: 2px;
  position: absolute;
  left: 9px;
  transition: transform 0.25s, opacity 0.2s;
}
.hamburger span { top: 19px; }
.hamburger span::before { content: ''; top: -7px; }
.hamburger span::after { content: ''; top: 7px; }
.hamburger span.open { background: transparent; }
.hamburger span.open::before { top: 0; transform: rotate(45deg); }
.hamburger span.open::after { top: 0; transform: rotate(-45deg); }

.nav-mobile {
  display: none;
  flex-direction: column;
  border-top: 1px solid var(--line-soft);
  padding: 12px 0;
}
.nav-mobile a {
  display: block;
  padding: 14px 24px;
  color: var(--ink);
  text-decoration: none;
  font-weight: 600;
  font-size: 16px;
}
.nav-mobile a:hover { background: var(--bg-warm); }

@media (max-width: 768px) {
  .nav-desktop { display: none; }
  .lang-switch.desktop { display: none; }
  .hamburger { display: block; }
  .nav-mobile { display: flex; }
}
</style>
